/* BD HERBS — "সম্প্রতি দেখা পণ্য" (sections/bd-recently-viewed.liquid)
   Product pages save their handle in localStorage "bdherbs_recently_viewed" (inline script in
   sections/bd-main-product.liquid). This element shows the others: when it nears the screen it fetches
   /products/<handle>?section_id=bd-card-fetch for each (max 12, in parallel) and places the returned
   product cards in the carousel track. A failed or empty response just leaves that product out. */
(() => {
  const KEY = 'bdherbs_recently_viewed';
  const OLD_KEY = 'bd-recently-viewed'; // first version of this feature
  const MAX = 12;

  // localStorage can throw (private mode, storage full, blocked) — the section then stays hidden
  const read = () => {
    try {
      let list = JSON.parse(localStorage.getItem(KEY) || '[]');
      const old = localStorage.getItem(OLD_KEY);
      if (old) {
        const oldList = JSON.parse(old);
        if (Array.isArray(oldList)) list = [...(Array.isArray(list) ? list : []), ...oldList];
        localStorage.removeItem(OLD_KEY);
      }
      if (!Array.isArray(list)) return [];
      return [...new Set(list.filter((h) => typeof h === 'string' && h))].slice(0, MAX);
    } catch (error) {
      return [];
    }
  };

  const write = (list) => {
    try {
      if (list.length) localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
      else localStorage.removeItem(KEY);
    } catch (error) {
      /* ignore */
    }
  };

  window.BDRecentlyViewed = window.BDRecentlyViewed || { read, clear: () => write([]) };

  if (customElements.get('bd-recently-viewed')) return;

  customElements.define(
    'bd-recently-viewed',
    class BdRecentlyViewed extends HTMLElement {
      connectedCallback() {
        if (this.bound) return; // set up once (a reconnect must not add a second listener / fetch)
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.bound = true;
        this.limit = Math.min(MAX, Number(this.dataset.limit) || 10);
        this.handles = read()
          .filter((h) => h !== this.dataset.current)
          .slice(0, this.limit);

        const clear = this.querySelector('.bd-sh-link');
        if (clear) {
          clear.setAttribute('role', 'button');
          clear.addEventListener('click', (event) => {
            event.preventDefault();
            write([]);
            this.hidden = true;
          });
        }

        if (!this.handles.length) {
          // Theme editor: show the loading cards so the section can still be styled
          if (this.hasAttribute('data-design-mode')) this.showSkeleton(Math.min(this.limit, 5));
          return;
        }

        this.showSkeleton(this.handles.length);
        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      showSkeleton(count) {
        const item =
          '<li class="bd-rv__item bd-rv__skel" aria-hidden="true">' +
          '<span class="bd-rv__skel-media"></span><span class="bd-rv__skel-line"></span>' +
          '<span class="bd-rv__skel-line bd-rv__skel-line--short"></span><span class="bd-rv__skel-button"></span></li>';
        this.track.innerHTML = item.repeat(Math.max(1, count));
        this.track.setAttribute('aria-busy', 'true');
        this.hidden = false;
        this.refreshCarousel();
      }

      productUrl(handle) {
        const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
        return `${root}products/${encodeURIComponent(handle)}?section_id=bd-card-fetch`;
      }

      // Returns the card element, or null (sold out, deleted, network error …)
      async fetchCard(handle) {
        try {
          const response = await fetch(this.productUrl(handle), { credentials: 'same-origin' });
          if (response.status === 404) return { handle, deleted: true };
          if (!response.ok) return null;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const wrapper = doc.querySelector('[data-bd-card]');
          if (!wrapper || !wrapper.querySelector('.card-wrapper')) return null;
          const li = document.createElement('li');
          li.className = 'bd-rv__item';
          li.dataset.handle = handle;
          li.append(...[...wrapper.childNodes].map((node) => document.importNode(node, true)));
          return { handle, li };
        } catch (error) {
          return null;
        }
      }

      async load() {
        const results = await Promise.all(this.handles.map((handle) => this.fetchCard(handle)));

        // Deleted products leave the saved list for good; sold-out ones stay (they may come back)
        const deleted = results.filter((r) => r && r.deleted).map((r) => r.handle);
        if (deleted.length) write(read().filter((h) => !deleted.includes(h)));

        const items = results.filter((r) => r && r.li).map((r) => r.li);
        this.track.removeAttribute('aria-busy');
        if (!items.length) {
          this.track.innerHTML = '';
          this.hidden = true;
          return;
        }
        this.track.replaceChildren(...items);
        this.hidden = false;
        this.refreshCarousel();
      }

      refreshCarousel() {
        const carousel = this.querySelector('bd-carousel');
        const update = () => carousel && typeof carousel.update === 'function' && carousel.update();
        requestAnimationFrame(update);
        // bd-carousel.js may still be loading; update once it is defined
        if (!customElements.get('bd-carousel')) customElements.whenDefined('bd-carousel').then(() => requestAnimationFrame(update));
      }
    }
  );
})();
